import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Segmented,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText, formatAmount, saveBlob } from '../format';

const { Text } = Typography;

const amountColumns = (t) => [
  {
    title: t('ab.debit'),
    dataIndex: 'debit',
    key: 'debit',
    width: 160,
    align: 'right',
    render: formatAmount,
  },
  {
    title: t('ab.credit'),
    dataIndex: 'credit',
    key: 'credit',
    width: 160,
    align: 'right',
    render: formatAmount,
  },
  {
    title: t('ab.balance'),
    dataIndex: 'balance',
    key: 'balance',
    width: 160,
    align: 'right',
    render: formatAmount,
  },
];

/**
 * Whether an account falls between two account prefixes — the same rule the
 * server's export uses (one bound alone means that account or class only).
 *
 * @param {string} account - Account
 * @param {string} fromText - First account or prefix
 * @param {string} toText - Last account or prefix
 * @returns {boolean} In range
 */
const inRange = (account, fromText, toText) => {
  const from = fromText || toText;
  const to = toText || fromText;
  if (!from) return true;
  const head = account.slice(0, Math.max(from.length, to.length));
  return head >= from && head.slice(0, to.length) <= to;
};

const digitsOnly = (value) => value.replace(/\D/g, '');

/**
 * One account broken down by partner, loaded when the row is opened.
 */
function PartnerBreakdown({ companyId, params, account }) {
  const { t } = useTranslation('clientReports');
  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-ab-tb-partner', companyId, params, account],
    queryFn: () =>
      API.clientReports.getAbTrialBalance(companyId, { ...params, account }),
  });
  return (
    <Table
      scroll={{ x: 'max-content' }}
      rowKey={(r) => r.key || '-'}
      size="small"
      loading={isLoading}
      pagination={false}
      dataSource={data?.data?.rows || []}
      columns={[
        {
          title: t('ab.analytic'),
          dataIndex: 'key',
          key: 'key',
          width: 130,
          render: (v) =>
            v || <Text type="secondary">{t('fccs.noPartner')}</Text>,
        },
        { title: t('ab.name'), dataIndex: 'name', key: 'name' },
        ...amountColumns(t),
      ]}
    />
  );
}

/**
 * Trial balance built from the received lines — the figures to set against
 * AB's own "Zbirna glavna knjiga" / "Bruto bilans".
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onOpenLines - Called with { account, period } to show the lines
 * @returns {JSX.Element} The trial balance
 */
export default function AbTrialBalanceTab({ companyId, onOpenLines }) {
  const { t } = useTranslation('clientReports');
  const [period, setPeriod] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const [scope, setScope] = useState('ytd');
  const [search, setSearch] = useState('');
  const [accountFrom, setAccountFrom] = useState('');
  const [accountTo, setAccountTo] = useState('');
  const [analytic, setAnalytic] = useState(true);

  const params = { year: period.year(), month: period.month() + 1, scope };
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-ab-tb', companyId, params],
    queryFn: () => API.clientReports.getAbTrialBalance(companyId, params),
    retry: false,
  });
  const result = data?.data;

  const needle = search.trim().toLowerCase();
  const ranged = Boolean(accountFrom || accountTo);
  const rows = (result?.rows || []).filter(
    (r) =>
      inRange(r.key, accountFrom, accountTo) &&
      (!needle ||
        r.key.startsWith(needle) ||
        r.name.toLowerCase().includes(needle))
  );
  const rangeTotals = rows.reduce(
    (sum, r) => ({ debit: sum.debit + r.debit, credit: sum.credit + r.credit }),
    { debit: 0, credit: 0 }
  );

  const exportMutation = useMutation({
    mutationFn: () =>
      API.clientReports.exportAbTrialBalance(companyId, {
        ...params,
        accountFrom: accountFrom || undefined,
        accountTo: accountTo || undefined,
        analytic,
      }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year: params.year })
      : error?.response?.data?.message || error?.message;

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          format="MM.YYYY"
          value={period}
          onChange={(value) => value && setPeriod(value.startOf('month'))}
        />
        <Segmented
          value={scope}
          onChange={setScope}
          options={[
            { value: 'ytd', label: t('ab.scopeYtd') },
            { value: 'month', label: t('ab.scopeMonth') },
          ]}
        />
        <Input
          allowClear
          style={{ width: 240 }}
          placeholder={t('ab.accountSearch')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Space>

      <Space wrap align="center" style={{ display: 'flex', marginBottom: 12 }}>
        <Input
          allowClear
          style={{ width: 140 }}
          placeholder={t('ab.accountFrom')}
          value={accountFrom}
          onChange={(e) => setAccountFrom(digitsOnly(e.target.value))}
        />
        <Input
          allowClear
          style={{ width: 140 }}
          placeholder={t('ab.accountTo')}
          value={accountTo}
          onChange={(e) => setAccountTo(digitsOnly(e.target.value))}
        />
        <Space size={6}>
          <Switch checked={analytic} onChange={setAnalytic} />
          <Text>{t('ab.withAnalytics')}</Text>
        </Space>
        <Button
          shape="round"
          icon={<FileExcelOutlined />}
          disabled={!result || !rows.length}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('ab.exportExcel')}
        </Button>
        <Text type="secondary">{t('ab.rangeHint')}</Text>
      </Space>

      {errorMessage && (
        <Alert
          type="warning"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 12 }}
        />
      )}

      {result && (
        <Space size="large" style={{ display: 'flex', marginBottom: 8 }}>
          <Text>{t('ab.accountsCount', { count: result.rows.length })}</Text>
          <Text>
            {t('ab.debit')}: <Text strong>{formatAmount(result.debit)}</Text>
          </Text>
          <Text>
            {t('ab.credit')}: <Text strong>{formatAmount(result.credit)}</Text>
          </Text>
          <Text type={Math.abs(result.balance) >= 0.01 ? 'danger' : undefined}>
            {t('ab.balance')}:{' '}
            <Text strong>{formatAmount(result.balance)}</Text>
          </Text>
        </Space>
      )}

      {result && ranged && (
        <Space size="large" style={{ display: 'flex', marginBottom: 8 }}>
          <Text type="secondary">
            {t('ab.inRange', { count: rows.length })}
          </Text>
          <Text>
            {t('ab.debit')}:{' '}
            <Text strong>{formatAmount(rangeTotals.debit)}</Text>
          </Text>
          <Text>
            {t('ab.credit')}:{' '}
            <Text strong>{formatAmount(rangeTotals.credit)}</Text>
          </Text>
          <Text>
            {t('ab.balance')}:{' '}
            <Text strong>
              {formatAmount(rangeTotals.debit - rangeTotals.credit)}
            </Text>
          </Text>
        </Space>
      )}

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="key"
        size="small"
        loading={isLoading}
        dataSource={rows}
        pagination={{ pageSize: 100, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (r) => (
            <PartnerBreakdown
              companyId={companyId}
              params={params}
              account={r.key}
            />
          ),
        }}
        columns={[
          { title: t('ab.account'), dataIndex: 'key', key: 'key', width: 100 },
          { title: t('ab.name'), dataIndex: 'name', key: 'name' },
          ...amountColumns(t),
          {
            key: 'open',
            width: 110,
            render: (_, r) => (
              <Button
                size="small"
                shape="round"
                onClick={() =>
                  onOpenLines({
                    account: r.key,
                    period: scope === 'month' ? period : null,
                  })
                }
              >
                {t('ab.openLines', { count: r.lines })}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}
